/**
 * Decides when a session break's title editor should open by itself, focused
 * with the default title selected, so naming a break the coach just added is
 * one keystroke instead of a click-then-type.
 *
 * `mostRecentlyAddedCalendarItem` is the signal, because it is re-stamped with
 * the *saved* item on the save's _FULFILLED — the point at which the pre-save
 * row is replaced under its real assignedId and the editor can mount against
 * something the update endpoint will accept.
 *
 * The ids already handled are remembered module-wide: the row a coach sees is
 * mounted and unmounted again by things that are not an add (the day list is
 * virtualized, and the month popover remounts on every open), and the tracker
 * outlives all of them. Without the registry each of those remounts would
 * steal focus and re-select the title.
 */

export const DEFAULT_SESSION_BREAK_TITLE = 'Session Break';

const autoFocusedAssignedIds = new Set();

/**
 * @param {?Object} content                        the calendar item being rendered
 * @param {?Object} mostRecentlyAddedCalendarItem  from calendar.data
 * @returns {boolean} true the first time this freshly added session break renders as saved
 */
export const shouldAutoFocusSessionBreakTitle = (
  content,
  mostRecentlyAddedCalendarItem,
) => {
  if (content?.type !== 'session_break') return false;
  /** A pre-save row has no server id yet; its auto-save is what produces one. */
  if (content.preSave || content.assignedId == null) return false;
  if (mostRecentlyAddedCalendarItem?.assignedId !== content.assignedId) return false;
  return !autoFocusedAssignedIds.has(content.assignedId);
};

/**
 * Records that this session break's title editor has been auto-opened once.
 *
 * @param {string|number} assignedId the saved item's id
 * @returns {void}
 */
export const markSessionBreakTitleAutoFocused = (assignedId) => {
  autoFocusedAssignedIds.add(assignedId);
};
